import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Select, TextInput } from "flowbite-react";
import { FieldLabel } from "../../../atoms/FieldLabel";
import {
  HiOutlineClipboardList,
  HiOutlineUsers,
  HiOutlineChatAlt2,
  HiOutlinePlus,
} from "react-icons/hi";
import { CharacterCounter } from "../../../atoms/CharacterCounter";
import { CustomButton } from "../../../atoms/CustomButton";
import { CopyToClipboardButton } from "../../../atoms/CopyToClipboardButton";
import SideDrawer from "../../../atoms/SideDrawer";
import HtmlEditor from "../../../atoms/HtmlEditor";
import NoTranslate from "../../../atoms/NoTranslate";
import SegmentedTabs, { SegmentedTab } from "../../../molecules/SegmentedTabs";
import NotesPanel from "../../../molecules/NotesPanel";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { useGetEntityUser } from "../../../../hooks/users";
import {
  TodoTask,
  TodoColumn,
  TodoBadge,
  UpdateTaskInput,
  TodoNoteAuthor,
  TodoAttachment,
  MAX_TODO_TASK_TITLE_LENGTH,
  MAX_TODO_TASK_DESCRIPTION_LENGTH,
  TODO_NOTE_LIMITS,
} from "../../../../types/TodoTypes";
import { resolveColumnId, isHtmlEmpty } from "./todoColumns";
import AssigneePicker, { PickedUser, PickedGroup } from "./AssigneePicker";
import TaskAttachments from "./TaskAttachments";
import { badgeSwatchStyle } from "./todoBadges";
import BadgePill from "./BadgePill";
import { formatShortId } from "./todoUrl";
import AddToAgendaButton from "../agenda/AddToAgendaButton";
import { taskDraft } from "../agenda/appointmentDraft";
import { useAgendaAccess } from "../../../../hooks/agenda/useAgendaAccess";

interface Props {
  task: TodoTask | null;
  columns: TodoColumn[];
  badges: TodoBadge[];
  customerId: string;
  entityId: string;
  boardId: string;
  resolvedUsers: Record<string, PickedUser>;
  resolvedGroups: Record<string, PickedGroup>;
  shareUrl?: string;
  boardAccessUserIds?: string[];
  onClose: () => void;
  onSave: (patch: UpdateTaskInput, done: () => void) => void;
  isSaving?: boolean;
}

type TabKey = "details" | "assignees" | "notes";

const TaskDetailModal: React.FC<Props> = ({
  task,
  columns,
  badges,
  customerId,
  entityId,
  boardId,
  resolvedUsers,
  resolvedGroups,
  shareUrl,
  boardAccessUserIds,
  onClose,
  onSave,
  isSaving = false,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);
  const canWrite = permissions.can(Cap.TASKS_BOARD_TASKS, Level.WRITE);
  const canDelete = permissions.can(Cap.TASKS_BOARD_TASKS, Level.DELETE);
  const { canCreate: canAddToAgenda } = useAgendaAccess(customerId, entityId);

  const { data: profile } = useGetEntityUser({
    customer_identification: customerId,
    entityIdentification: entityId,
    user_identification: user.user_identification,
  });

  const currentAuthor = (): TodoNoteAuthor => {
    const name =
      [profile?.user_first_name, profile?.user_last_name]
        .filter(Boolean)
        .join(" ") ||
      profile?.user_email ||
      user.user_identification ||
      "";
    return { id: user.user_identification || "", name };
  };

  const authorLabel = (
    author?: { id?: string; name?: string } | null
  ): string => {
    if (!author) return "";
    const resolved = author.id ? resolvedUsers[author.id]?.name : "";
    if (resolved) return resolved;
    if (author.name && author.name !== author.id) return author.name;
    return "";
  };

  const [activeTab, setActiveTab] = useState<TabKey>("details");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionProblem, setDescriptionProblem] = useState<
    string | undefined
  >();
  const handleDescriptionValidation = useCallback(
    (_isValid: boolean, message?: string) => setDescriptionProblem(message),
    []
  );
  const [columnId, setColumnId] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [userIds, setUserIds] = useState<string[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [badgeIds, setBadgeIds] = useState<string[]>([]);
  const [showBadgeMenu, setShowBadgeMenu] = useState(false);

  useEffect(() => {
    if (task) {
      setActiveTab("details");
      setTitle(task.title || "");
      setDescription(task.description || "");
      setColumnId(resolveColumnId(task.column_id, columns));
      setDueAt(task.due_at || "");
      setUserIds(task.user_ids || []);
      setGroupIds(task.group_ids || []);
      setBadgeIds(task.badge_ids || []);
      setShowBadgeMenu(false);
    }
  }, [task?.task_id]);

  if (!task) return null;

  const notes = Array.isArray(task.notes) ? task.notes : [];
  const assigneeCount = userIds.length + groupIds.length;
  // Vide tant que le back ne sert pas de short id : la ligne disparait alors
  // entierement plutot que d'afficher une reference vide.
  const shortRef = formatShortId(task.short_id);

  const buildDirtyPatch = (): UpdateTaskInput => {
    const patch: UpdateTaskInput = {};
    const cleanTitle = title.trim();
    if (cleanTitle && cleanTitle !== task.title) patch.title = cleanTitle;
    const normalizedDesc = isHtmlEmpty(description) ? "" : description;
    if (normalizedDesc !== (task.description || ""))
      patch.description = normalizedDesc;
    if (columnId !== resolveColumnId(task.column_id, columns))
      patch.column_id = columnId;
    if (dueAt !== (task.due_at || "")) patch.due_at = dueAt || null;
    const sameIds = (a: string[], b: string[]) =>
      a.length === b.length && a.every((x) => b.includes(x));
    if (!sameIds(userIds, task.user_ids || [])) patch.user_ids = userIds;
    if (!sameIds(groupIds, task.group_ids || [])) patch.group_ids = groupIds;
    if (!sameIds(badgeIds, task.badge_ids || [])) patch.badge_ids = badgeIds;
    return patch;
  };

  const dirtyPatch = buildDirtyPatch();
  const hasUnsavedChanges = Object.keys(dirtyPatch).length > 0;
  // A stored text over the limit passes as long as it is not sent again.
  const savingRefusal =
    dirtyPatch.description !== undefined ? descriptionProblem : undefined;

  const handleSave = () => {
    if (isSaving) return;
    const patch = buildDirtyPatch();
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    if (!canWrite) return;
    onSave(patch, onClose);
  };

  const handleAttachmentsChange = (attachments: TodoAttachment[]) => {
    if (!canWrite) return;
    onSave({ attachments }, () => {});
  };

  const handleDeleteNote = (index: number) => {
    if (isSaving) return;
    if (!canDelete) return;
    onSave({ delete_note: { index } }, () => {});
  };

  const tabs: SegmentedTab<TabKey>[] = [
    {
      key: "details",
      icon: <HiOutlineClipboardList size={16} />,
      label: t("todos.task.tabs.details") as string,
    },
    {
      key: "assignees",
      icon: <HiOutlineUsers size={16} />,
      label: t("todos.task.tabs.assignees") as string,
      badge: assigneeCount,
    },
    {
      key: "notes",
      icon: <HiOutlineChatAlt2 size={16} />,
      label: t("todos.task.tabs.notes") as string,
      badge: notes.length,
    },
  ];

  const drawerTitle = task.title
    ? `${t("todos.task.title")} · ${task.title}`
    : (t("todos.task.title") as string);

  return (
    <SideDrawer
      open={!!task}
      onClose={onClose}
      title={drawerTitle}
      icon={HiOutlineClipboardList}
      footer={
        <div className="sticky bottom-0 left-0 right-0 -mx-4 -mb-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2">
            <div
              className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-200 ${
                hasUnsavedChanges
                  ? "bg-amber-500 border-amber-500"
                  : "bg-green-500 border-green-500"
              }`}
            />
            <span
              role={savingRefusal ? "alert" : undefined}
              className={`text-xs font-semibold ${
                hasUnsavedChanges ? "text-amber-700" : "text-green-800"
              }`}
            >
              {savingRefusal ??
                (hasUnsavedChanges
                  ? t("todos.task.unsavedChanges")
                  : t("todos.task.allSaved"))}
            </span>
          </div>

          <div className="flex gap-3">
            <CustomButton
              variant="secondary"
              title={t("common.cancel") as string}
              onClick={onClose}
            />
            <CustomButton
              variant={
                isSaving || !hasUnsavedChanges || !canWrite || savingRefusal
                  ? "secondary"
                  : "primary"
              }
              isLoading={isSaving}
              disabled={
                isSaving ||
                !hasUnsavedChanges ||
                !canWrite ||
                Boolean(savingRefusal)
              }
              title={t("common.saveShort") as string}
              onClick={handleSave}
            />
          </div>
        </div>
      }
    >
      {(shortRef || canAddToAgenda) && (
        <div className="mb-4 flex flex-wrap items-center gap-2 border-b border-gray-100 pb-3">
          {shortRef && (
            <>
              <span className="text-xs text-gray-500">
                {t("todos.share.taskIdLabel")}:
              </span>
              <span className="select-all rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-sm font-medium tabular-nums text-gray-700">
                <NoTranslate>{shortRef}</NoTranslate>
              </span>
              {shareUrl && (
                <CopyToClipboardButton
                  variant="secondary"
                  size="xs"
                  fullWidth={false}
                  textToCopy={shareUrl}
                  label={t("todos.share.task") as string}
                  onSuccess={() =>
                    toast.success(t("todos.share.copied") as string)
                  }
                  onError={() => toast.error(t("common.copyFailed") as string)}
                />
              )}
            </>
          )}
          <AddToAgendaButton
            size="xs"
            getDraft={(author) =>
              taskDraft(
                { title, due_at: dueAt, user_ids: userIds },
                author,
                shareUrl
              )
            }
          />
        </div>
      )}

      <SegmentedTabs tabs={tabs} active={activeTab} onChange={setActiveTab} />
      <p className="mt-3 text-sm text-gray-600">
        {t(`todos.task.tabDescriptions.${activeTab}`)}
      </p>

      <div className="mt-4">
        {activeTab === "details" && (
          <div className="space-y-6">
            <div>
              <FieldLabel>{t("todos.task.titleLabel")}</FieldLabel>
              <TextInput
                type="text"
                value={title}
                maxLength={MAX_TODO_TASK_TITLE_LENGTH}
                disabled={!canWrite}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full"
              />
              <CharacterCounter
                currentLength={title.length}
                maxLength={MAX_TODO_TASK_TITLE_LENGTH}
                className="mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                {t("todos.task.badges")}
              </label>
              {badges.length === 0 ? (
                <p className="text-sm text-gray-400">
                  {t("todos.task.noBadgesConfigured")}
                </p>
              ) : (
                (() => {
                  const selectedBadges = badges.filter((b) =>
                    badgeIds.includes(b.id)
                  );
                  const availableBadges = badges.filter(
                    (b) => !badgeIds.includes(b.id)
                  );
                  return (
                    <>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {selectedBadges.map((b) => (
                          <BadgePill
                            key={b.id}
                            badge={b}
                            size="md"
                            onRemove={
                              canWrite
                                ? () =>
                                    setBadgeIds((prev) =>
                                      prev.filter((x) => x !== b.id)
                                    )
                                : undefined
                            }
                            removeLabel={t("todos.task.removeBadge") as string}
                          />
                        ))}

                        {canWrite && availableBadges.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setShowBadgeMenu((s) => !s)}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border border-dashed transition-colors ${
                              showBadgeMenu
                                ? "border-blue-300 text-blue-600 bg-blue-50"
                                : "border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700"
                            }`}
                          >
                            <HiOutlinePlus size={12} />
                            {t("common.add")}
                          </button>
                        )}
                      </div>

                      {showBadgeMenu && availableBadges.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5 rounded-lg border border-gray-100 bg-gray-50 p-2">
                          {availableBadges.map((b) => (
                            <button
                              key={b.id}
                              type="button"
                              onClick={() => {
                                setBadgeIds((prev) => [...prev, b.id]);
                                if (availableBadges.length === 1)
                                  setShowBadgeMenu(false);
                              }}
                              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-white border border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                            >
                              <span
                                style={badgeSwatchStyle(b.color)}
                                className="w-2.5 h-2.5 rounded-full"
                              />
                              <NoTranslate>{b.label}</NoTranslate>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <FieldLabel>{t("todos.task.column")}</FieldLabel>
                <Select
                  value={columnId}
                  disabled={!canWrite}
                  onChange={(e) => setColumnId(e.target.value)}
                >
                  {columns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <FieldLabel>{t("todos.task.dueAt")}</FieldLabel>
                <TextInput
                  type="date"
                  value={dueAt}
                  disabled={!canWrite}
                  onChange={(e) => setDueAt(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                {t("todos.task.description")}
              </label>
              <HtmlEditor
                value={description}
                onChange={setDescription}
                disabled={!canWrite}
                placeholder={t("todos.task.descriptionPlaceholder") as string}
                minHeight="320px"
                maxHeight="560px"
                maxLength={MAX_TODO_TASK_DESCRIPTION_LENGTH}
                onValidationChange={handleDescriptionValidation}
                showCharacterCount
                className="border-gray-300"
              />
            </div>

            <div className="border-t border-gray-100 pt-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("todos.task.attachments.label")}
              </label>
              <TaskAttachments
                customerId={customerId}
                entityId={entityId}
                boardId={boardId}
                taskId={task.task_id}
                attachments={task.attachments || []}
                onChange={handleAttachmentsChange}
                disabled={!canWrite}
                canDelete={canDelete}
              />
            </div>
          </div>
        )}

        {activeTab === "assignees" && (
          <div>
            <p className="text-sm text-gray-500 mb-4">
              {t("todos.task.assigneesHelp")}
            </p>
            <AssigneePicker
              customerId={customerId}
              entityId={entityId}
              userIds={userIds}
              groupIds={groupIds}
              resolvedUsers={resolvedUsers}
              resolvedGroups={resolvedGroups}
              onChangeUsers={setUserIds}
              onChangeGroups={setGroupIds}
              allowedTeamUserIds={boardAccessUserIds}
              disabled={!canWrite}
            />
          </div>
        )}

        {activeTab === "notes" && (
          <div>
            <NotesPanel
              notes={notes}
              onAdd={(text, done) =>
                onSave({ add_note: text, author: currentAuthor() }, done)
              }
              onEdit={(index, text, done) =>
                onSave({ edit_note: { index, text } }, done)
              }
              onDelete={(index) => handleDeleteNote(index)}
              disabled={!canWrite}
              canDelete={canDelete}
              maxLength={TODO_NOTE_LIMITS.max}
              minLength={TODO_NOTE_LIMITS.min}
              isSaving={isSaving}
              resolveAuthor={(author) => {
                const info = author?.id ? resolvedUsers[author.id] : undefined;
                if (info) return info;
                const name = authorLabel(author);
                return name ? { name } : undefined;
              }}
              threadHeightClass=""
            />
          </div>
        )}
      </div>
    </SideDrawer>
  );
};

export default TaskDetailModal;
